import {
  AccessRole,
  AccessRoleCatalog,
  BASE_ACCESS_ROLE,
} from '@trustero/trustero-api-web/lib/authz/authz_pb'
import { USER_ROLE } from '@trustero/trustero-api-web/lib/account/account_pb'
import {
  CAPABILITY,
  PERMISSION_RULE,
} from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { UserGroupRecord } from '@trustero/trustero-api-web/lib/usergroup/usergroup_pb'
import { getEnumKeyName } from 'src/Utils/helpers/string.helpers'
import {
  indexCatalog,
  resolveCapabilityRule,
  resolveDomainRole,
  getPermissionOverrides,
} from 'src/pages/Users/userRoles/userRoles.helpers'
import type {
  ImpersonationInfo,
  PermissionInfo,
  Permissions,
} from 'src/context/authContext'

export const IMPERSONATE_ROLE_PARAM = 'impersonate_role'
export const IMPERSONATE_GROUPS_PARAM = 'impersonate_groups'
export const IMPERSONATE_ALLOWED_ROUTES_PARAM = 'impersonate_routes'
export const IMPERSONATION_SESSION_STORAGE_KEY =
  'trustero_impersonation_session'

const IMPERSONATION_PARAMS = [
  IMPERSONATE_ROLE_PARAM,
  IMPERSONATE_GROUPS_PARAM,
  IMPERSONATE_ALLOWED_ROUTES_PARAM,
]

// Preview mode lives in sessionStorage so it stays scoped to the tab it was
// opened in. Every access is guarded because sessionStorage throws in some
// privacy modes - losing it only costs the preview surviving a reload.
export const readImpersonationSession = (): ImpersonationInfo | undefined => {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw) as ImpersonationInfo
    return parsed?.isImpersonating ? parsed : undefined
  } catch (e) {
    return undefined
  }
}

export const writeImpersonationSession = (info: ImpersonationInfo): void => {
  try {
    sessionStorage.setItem(
      IMPERSONATION_SESSION_STORAGE_KEY,
      JSON.stringify(info),
    )
  } catch (e) {
    // ignore
  }
}

export const clearImpersonationSession = (): void => {
  try {
    sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}

export const baseAccessRoleToUserRole = (base: BASE_ACCESS_ROLE): USER_ROLE => {
  switch (base) {
    case BASE_ACCESS_ROLE.ADMIN:
      return USER_ROLE.ADMIN
    case BASE_ACCESS_ROLE.USER:
      return USER_ROLE.USER
    case BASE_ACCESS_ROLE.AUDITOR:
    case BASE_ACCESS_ROLE.AUDITOR_QC:
      return USER_ROLE.AUDITOR
    case BASE_ACCESS_ROLE.READ_ONLY:
    case BASE_ACCESS_ROLE.NO_ACCESS:
      return USER_ROLE.READONLY
    case BASE_ACCESS_ROLE.TP_PUBLISHER:
      return USER_ROLE.TRUSTPORTAL_PUBLISHER
    case BASE_ACCESS_ROLE.TP_APPROVER:
      return USER_ROLE.TRUSTPORTAL_APPROVER
    default:
      return USER_ROLE.READONLY
  }
}

export type EffectiveLabelRestrictions = {
  allowLabelIds: string[]
  denyLabelIds: string[]
}

// The TypeScript mirror of rbac.CombineLabelRestrictions: union the grants of every group,
// with deny winning so the answer does not depend on group order. A group's flat labelIds
// list means allow, matching how the service reads a request that sends only that.
export const combineLabelRestrictions = (
  groupLabels: Array<{
    labelIds?: string[]
    allowLabelIds?: string[]
    denyLabelIds?: string[]
  }> = [],
): EffectiveLabelRestrictions => {
  const allow = new Set<string>()
  const deny = new Set<string>()

  groupLabels.forEach((gl) => {
    gl.denyLabelIds?.forEach((id) => {
      if (id) deny.add(id)
    })
    const allowed =
      gl.allowLabelIds?.length || gl.denyLabelIds?.length
        ? gl.allowLabelIds
        : gl.labelIds
    allowed?.forEach((id) => {
      if (id) allow.add(id)
    })
  })

  return {
    allowLabelIds: Array.from(allow).filter((id) => !deny.has(id)),
    denyLabelIds: Array.from(deny),
  }
}

export const computeEffectivePermissions = (
  accessRole: AccessRole,
  selectedGroups: UserGroupRecord[] = [],
  catalog: AccessRoleCatalog,
): Permissions => {
  const index = indexCatalog(catalog)
  const overrides = getPermissionOverrides(accessRole)

  const capabilitiesMap: Partial<Record<CAPABILITY, PermissionInfo>> = {}

  index.domains.forEach((domain) => {
    const domainRole = resolveDomainRole(index, accessRole, domain)
    const domainCaps = index.capabilitiesByDomain.get(domain.domain) || []

    domainCaps.forEach((cap) => {
      const rule = resolveCapabilityRule(
        index,
        overrides,
        domain.domain,
        domainRole,
        cap.capability,
      )
      capabilitiesMap[cap.capability] = {
        rule,
        capabilityName: cap.name || getEnumKeyName(CAPABILITY, cap.capability),
        ruleName: getEnumKeyName(PERMISSION_RULE, rule),
      }
    })
  })

  // Groups carry which objects, not what may be done to them: a group never adds a
  // capability the role withheld. Mirrors the deletion of ApplyLabelCapabilityPromotions.
  const effectiveLabels = combineLabelRestrictions(
    selectedGroups.map((g) => ({
      labelIds: g.getLabelIdsList() || [],
      allowLabelIds: g.getAllowLabelIdsList() || [],
      denyLabelIds: g.getDenyLabelIdsList() || [],
    })),
  )

  return {
    version: Date.now(),
    baseAccessRole: accessRole.getBaseAccessRole(),
    hasRoleOverrides:
      Object.values(accessRole.getDomainOverrides()?.toObject() || {}).some(
        (value) => value !== 0,
      ) ||
      (accessRole.getPermissionOverrides()?.getPermissionsList().length || 0) >
        0,
    capabilities: capabilitiesMap,
    allowLabelIds: effectiveLabels.allowLabelIds,
    denyLabelIds: effectiveLabels.denyLabelIds,
  }
}

const parseCsvParam = (value: string | null): string[] | undefined => {
  if (!value) return undefined
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

export const parseImpersonationParams = (
  search: string,
): { roleId?: string; groupIds?: string[]; allowedRoutes?: string[] } => {
  const params = new URLSearchParams(search)
  return {
    roleId: params.get(IMPERSONATE_ROLE_PARAM) || undefined,
    groupIds: parseCsvParam(params.get(IMPERSONATE_GROUPS_PARAM)),
    allowedRoutes: parseCsvParam(params.get(IMPERSONATE_ALLOWED_ROUTES_PARAM)),
  }
}

// Drops every impersonation param, returning a search string ready to assign
// (leading '?' included, or empty when nothing is left).
export const stripImpersonationParams = (search: string): string => {
  const params = new URLSearchParams(search)
  IMPERSONATION_PARAMS.forEach((param) => params.delete(param))
  const searchStr = params.toString()
  return searchStr ? `?${searchStr}` : ''
}

export const buildImpersonateUrl = (
  baseUrl: string,
  roleId: string,
  groupIds: string[] = [],
  allowedRoutes?: string[],
): string => {
  const url = new URL(baseUrl, window.location.origin)
  url.searchParams.set(IMPERSONATE_ROLE_PARAM, roleId)
  if (groupIds.length > 0) {
    url.searchParams.set(IMPERSONATE_GROUPS_PARAM, groupIds.join(','))
  } else {
    url.searchParams.delete(IMPERSONATE_GROUPS_PARAM)
  }
  if (allowedRoutes && allowedRoutes.length > 0) {
    url.searchParams.set(
      IMPERSONATE_ALLOWED_ROUTES_PARAM,
      allowedRoutes.join(','),
    )
  } else {
    url.searchParams.delete(IMPERSONATE_ALLOWED_ROUTES_PARAM)
  }
  return url.pathname + url.search + url.hash
}

export const inferPreviewRoutes = (
  pathname: string,
): { allowedRoutes?: string[] } => {
  if (/\/controls(\/|$)/.test(pathname)) {
    return {
      allowedRoutes: ['controls/index', 'controls/show'],
    }
  }
  if (/\/policies(\/|$)/.test(pathname)) {
    return {
      allowedRoutes: ['policies/index', 'policies/show'],
    }
  }
  return {}
}

// The page a preview session falls back to: the first allowed route, scoped to
// the account in the URL when there is one.
export const previewHomeRoute = (
  pageContext: string | undefined,
  allowedRoutes: string[],
): string => {
  const firstRoute = allowedRoutes[0].split('?')[0]
  return pageContext ? `/${pageContext}/${firstRoute}` : `/${firstRoute}`
}

const normalizeRoute = (route: string): string =>
  route.replace(/^\//, '').split('?')[0].split('#')[0]

export const isPathAllowedInPreview = (
  pathname: string,
  pageContext?: string,
  allowedRoutes?: string[],
): boolean => {
  if (!allowedRoutes || allowedRoutes.length === 0) {
    return true
  }

  let relativePath = pathname
  if (pageContext) {
    const prefix = `/${pageContext}`
    if (relativePath.startsWith(prefix)) {
      relativePath = relativePath.slice(prefix.length)
    }
  } else {
    relativePath = relativePath.replace(/^\/[^/]+(\/|$)/, '')
  }

  relativePath = normalizeRoute(relativePath)

  if (!relativePath) {
    return false
  }

  return allowedRoutes.some((route) => {
    const cleanRoute = normalizeRoute(route)
    return (
      relativePath === cleanRoute ||
      relativePath.startsWith(`${cleanRoute}/`) ||
      relativePath === cleanRoute.split('/')[0]
    )
  })
}
